/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

/** A map of arrays. */
class ArrayMap<K, T> {
  private map = new Map<K, T[]>()

  /** Adds an item under a key. */
  add(key: K, item: T) {
    if (this.map.has(key)) this.map.get(key)!.push(item)
    else this.map.set(key, [item])
  }

  /*
   * Removes an item under a key.
   * If there are multiple instances (by reference) each one will be removed.
   */
  remove(key: K, item: T) {
    if (!this.map.has(key)) return
    const newArray = this.map.get(key)!.filter(c => c !== item)

    if (newArray.length > 0) this.map.set(key, newArray)
    else this.map.delete(key)
  }

  getForKey = (key: K) => this.map.get(key) ?? []
}

/** Design pattern to listen for and dispatch events by a string "type". */
export class EventEmitter {
  private cbs = new ArrayMap<string, (args: any) => any>()

  /** Add a callback function for an event type. */
  on = (type: string, cb: (args: any) => any) => {
    this.cbs.add(type, cb)
  }

  /** Remove a callback function for an event type. */
  off = (type: string, cb: (args: any) => any) => this.cbs.remove(type, cb)

  /** Execute all callbacks for an event type. */
  emit = (type: string, ...args: any) => {
    this.cbs.getForKey(type).forEach(cb => cb.apply(this, args))
  }

  /** Add a callback function for an event type - to be fired only once. */
  once(type: string, cb: (args: any) => any) {
    const onceCb = (...args: any) => {
      cb.apply(this, args)
      this.off(type, onceCb)
    }
    this.on(type, onceCb)
  }

  trigger(...args: any) {
    // eslint-disable-next-line prefer-spread
    return this.emit.apply(this, args)
  }

  /**
   * Returns a promise which is resolved when the next occurrence of a given
   * event type is emitted.
   */
  until = (type: string) => new Promise(resolve => this.once(type, resolve))
}
